import React, { useEffect, useState, useRef, useCallback } from 'react';
import { Icon } from '@iconify/react';

import NoData from '../../components/common/NoData'
import { getUserLogs } from '../../services/user/dashboard.services';
const activityDots = {
  "primary": "tw-bg-[#4361ee] tw-shadow-[0_0_0_3px_rgba(67,_97,_238,_0.2)] activity-dot-primary",
  "success": "tw-bg-[#28a745] tw-shadow-[0_0_0_3px_rgba(40,_167,_69,_0.2)] activity-dot-success",
  "warning": "tw-bg-[#ffc107] tw-shadow-[0_0_0_3px_rgba(255,_193,_7,_0.2)] activity-dot-warning"
};


const PAGE_SIZE = 20;

const HIDDEN_ACTIVITY_LOG_NAMES = new Set(['recommendation_impression', 'recommendation_click']);

const SURFACE_LABELS = {
  learner_dashboard: 'Learner dashboard',
  dashboard_recommended_courses: 'Dashboard recommended courses',
  course_catalog_recommended: 'Course recommendations',
  course_catalog: 'Course catalog',
};

const formatSurfaceLabel = (surface = '') => {
  if (!surface) return 'the platform';
  return SURFACE_LABELS[surface] || surface.replace(/_/g, ' ').replace(/\b\w/g, (m) => m.toUpperCase());
};

const formatActivity = (activity) => {
  if (!activity) {
    return { title: '', description: '' };
  }

  if (activity.log_name !== 'recommendation_impression') {
    return { title: activity.log_name, description: activity.log_description };
  }

  let parsedDescription = {};
  try {
    parsedDescription =
      typeof activity.log_description === 'string'
        ? JSON.parse(activity.log_description)
        : activity.log_description || {};
  } catch (error) {
    parsedDescription = {};
  }

  return {
    title: 'Recommendation viewed',
    description: `You viewed recommendations on ${formatSurfaceLabel(parsedDescription.surface)}.`,
  };
};

function Activity() {
  const [activities, setActivities] = useState([]);
  const [loading, setLoading] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [initialLoad, setInitialLoad] = useState(true);
  const listRef = useRef(null);
  const pageRef = useRef(1);
  const hasMoreRef = useRef(true);
  const loadingRef = useRef(false);

  const fetchActivities = useCallback(async (page = 1, append = false) => {
    if (loadingRef.current) return;

    loadingRef.current = true;
    setLoading(true);

    try {
      const response = await getUserLogs({ page, limit: PAGE_SIZE });

      if (response.success && response.data) {
        const { logs, pagination } = response.data;
        const visibleLogs = (logs || []).filter(
          (log) => !HIDDEN_ACTIVITY_LOG_NAMES.has(log.log_name)
        );

        setActivities((prev) => (append ? [...prev, ...visibleLogs] : visibleLogs));
        pageRef.current = pagination?.currentPage ?? page;
        hasMoreRef.current = Boolean(pagination?.hasMore);
        setHasMore(hasMoreRef.current);
      }
    } catch (error) {
      console.error('Error fetching activities:', error);
    } finally {
      loadingRef.current = false;
      setLoading(false);
      setInitialLoad(false);
    }
  }, []);

  useEffect(() => {
    fetchActivities(1, false);
  }, [fetchActivities]);

  const loadMore = useCallback(() => {
    if (loadingRef.current || !hasMoreRef.current) return;
    fetchActivities(pageRef.current + 1, true);
  }, [fetchActivities]);

  const handleScroll = useCallback(() => {
    const listEl = listRef.current;
    if (!listEl || loadingRef.current || !hasMoreRef.current) return;

    const nearBottom =
      listEl.scrollTop + listEl.clientHeight >= listEl.scrollHeight - 48;

    if (nearBottom) {
      loadMore();
    }
  }, [loadMore]);

  const getTimeAgo = (timestamp) => {
    if (!timestamp) return '';

    const now = new Date();
    const past = new Date(timestamp);
    const diffInSeconds = Math.floor((now - past) / 1000);

    if (diffInSeconds < 60) {
      return `${diffInSeconds} seconds ago`;
    }

    const diffInMinutes = Math.floor(diffInSeconds / 60);
    if (diffInMinutes < 60) {
      return `${diffInMinutes} ${diffInMinutes === 1 ? 'minute' : 'minutes'} ago`;
    }

    const diffInHours = Math.floor(diffInMinutes / 60);
    if (diffInHours < 24) {
      return `${diffInHours} ${diffInHours === 1 ? 'hour' : 'hours'} ago`;
    }

    const diffInDays = Math.floor(diffInHours / 24);
    if (diffInDays < 7) {
      return `${diffInDays} ${diffInDays === 1 ? 'day' : 'days'} ago`;
    }

    const diffInWeeks = Math.floor(diffInDays / 7);
    if (diffInWeeks < 4) {
      return `${diffInWeeks} ${diffInWeeks === 1 ? 'week' : 'weeks'} ago`;
    }

    const diffInMonths = Math.floor(diffInDays / 30);
    if (diffInMonths < 12) {
      return `${diffInMonths} ${diffInMonths === 1 ? 'month' : 'months'} ago`;
    }

    const diffInYears = Math.floor(diffInDays / 365);
    return `${diffInYears} ${diffInYears === 1 ? 'year' : 'years'} ago`;
  };

  return (
    <div className="ui-card tw-flex tw-h-[25rem] tw-min-w-0 tw-flex-col tw-overflow-hidden tw-rounded-xl tw-border tw-border-slate-200 tw-bg-white tw-shadow-sm max-[767px]:tw-h-auto max-[767px]:tw-overflow-visible">
      <div className="ui-card-header tw-flex tw-min-h-12 tw-shrink-0 tw-items-center tw-border-b tw-border-slate-100 tw-bg-white tw-px-4 tw-py-3">
        <h5 className="tw-m-0 tw-flex tw-items-center tw-gap-2 tw-text-sm tw-font-semibold tw-text-slate-900">
          <Icon icon="mdi:clock-outline" className="tw-text-brand-600" width={20} height={20} />
          <span>Recent Activity</span>
        </h5>
      </div>
      <div className="ui-card-body tw-min-h-0 tw-flex-1 max-[767px]:tw-flex-none">
        <div
          className="tw-flex tw-h-full tw-flex-col tw-gap-2 tw-overflow-y-auto tw-p-3 [scrollbar-width:thin] max-[767px]:tw-h-auto max-[767px]:tw-overflow-visible"
          ref={listRef}
          onScroll={handleScroll}
        >
          {initialLoad ? (
            <div className="tw-text-center tw-py-6">
              <div className="!tw-text-[var(--primary-color)] tw-inline-block tw-shrink-[0] tw-animate-[tw-spin_1s_linear_infinite] tw-rounded-full tw-border-[2px] tw-border-[currentColor] tw-border-r-[transparent] tw-w-[1em] tw-h-[1em] ui-spinner ui-spinner-sm tw-text-brand-600" role="status">
                <span className="tw-sr-only">Loading...</span>
              </div>
              <p className="!tw-text-[var(--secondary-color)] !tw-text-[12px] tw-mt-2 tw-text-slate-500 tw-text-sm">Loading activities...</p>
            </div>
          ) : (
            <>
              {activities.map((activity) => {
                const formattedActivity = formatActivity(activity);
                return (
                  <div
                    key={activity.id}
                    className="activity-item tw-flex tw-items-start tw-gap-3 tw-rounded-lg tw-border tw-border-slate-200 tw-bg-white tw-p-3 tw-transition-colors hover:tw-border-brand-400"
                  >
                    <div className={`tw-w-[10px] tw-h-[10px] tw-rounded-[50%] tw-shrink-[0] tw-mt-[6px] activity-dot ${activityDots[activity.status] || activityDots.primary}`} />
                    <div className="[flex:1] tw-min-w-[0] activity-content">
                      <div className="tw-font-semibold tw-mb-1 tw-text-[#333] tw-text-[0.875rem] activity-title">{formattedActivity.title}</div>
                      <div className="tw-text-[0.8125rem] tw-text-[#666] tw-leading-[1.4] activity-description tw-mb-1">{formattedActivity.description}</div>
                      <div className="tw-text-[0.75rem] tw-flex tw-items-center !tw-text-[var(--secondary-color)] activity-time tw-text-slate-500">
                        <Icon icon="mdi:clock-outline" className="tw-me-1" width="14" height="14" />
                        {getTimeAgo(activity.createdAt || activity.created_at)}
                      </div>
                    </div>
                  </div>
                );
              })}

              {loading && (
                <div className="tw-text-center tw-py-4">
                  <div className="!tw-text-[var(--primary-color)] tw-inline-block tw-shrink-[0] tw-animate-[tw-spin_1s_linear_infinite] tw-rounded-full tw-border-[2px] tw-border-[currentColor] tw-border-r-[transparent] tw-w-[1em] tw-h-[1em] ui-spinner ui-spinner-sm tw-text-brand-600" role="status">
                    <span className="tw-sr-only">Loading...</span>
                  </div>
                  <p className="!tw-text-[var(--secondary-color)] !tw-text-[12px] tw-mt-2 tw-text-slate-500 tw-text-sm">Loading more activities...</p>
                </div>
              )}

              {hasMore && activities.length > 0 && !loading && (
                <button
                  type="button"
                  className="tw-hidden tw-self-center tw-rounded-md tw-border tw-border-brand-200 tw-bg-white tw-px-4 tw-py-2 tw-text-sm tw-font-medium tw-text-brand-600 hover:tw-bg-brand-50 max-[767px]:tw-inline-flex"
                  onClick={loadMore}
                >
                  Load more activity
                </button>
              )}

              {!hasMore && activities.length > 0 && (
                <div className="tw-text-center tw-py-4">
                  <p className="!tw-text-[var(--secondary-color)] !tw-text-[12px] tw-text-slate-500 tw-text-sm">No more activities to load</p>
                </div>
              )}

              {activities.length === 0 && !loading && <NoData caption="No activity found" />}
            </>
          )}
        </div>
      </div>
    </div>
  );
}

export default Activity;
